/**
 * XRWebGLPresentationBackend — Production WebGL XR presentation backend.
 *
 * Wraps Three.js renderer.xr (WebXRManager) for WebGL-based XR sessions.
 * This is the default and most widely supported XR path (Quest, etc.).
 */

import type { EngineRenderer } from '../../render/Renderer.js';
import type { IXRPresentationBackend, XrViewRenderTarget, DepthRange } from './IXRPresentationBackend.js';
import { Logger } from '../../core/Logger.js';

export class XRWebGLPresentationBackend implements IXRPresentationBackend {
  readonly binding = 'webgl' as const;
  readonly depthRange: DepthRange = 'neg1to1';

  private _renderer: EngineRenderer;
  private _supportsLayers = false;
  private _layerTypes = new Set<string>(['projection']);
  // Reusable per-view buffers — stereo XR calls beginFrame() once per frame
  // with 2 views. Allocating fresh Float32Array(16)s every call burns ~360
  // arrays/second at 90 Hz (2 matrices × 2 views). Mirror the WebGPU
  // backend's pattern and grow lazily if a future runtime reports >2 views.
  private _viewMatrices: Float32Array[] = [new Float32Array(16), new Float32Array(16)];
  private _projMatrices: Float32Array[] = [new Float32Array(16), new Float32Array(16)];
  // Single scratch viewport reused across views. Fields are overwritten
  // each call, so consumers must snapshot if they need the value to
  // survive across beginFrame() — matches WebGPU backend behaviour.
  private _viewports: { x: number; y: number; width: number; height: number }[] = [];

  constructor(renderer: EngineRenderer) {
    this._renderer = renderer;
  }

  get supportsLayers(): boolean {
    return this._supportsLayers;
  }

  get layerTypes(): Set<string> {
    return this._layerTypes;
  }

  async attachSession(_session: XRSession): Promise<void> {
    // Three.js handles the actual WebGL XR setup via setSession
    // The XRModule calls setSession separately, but we detect layer support here
    const nativeXr = this._renderer.native.xr;

    // Detect if Layers API is supported
    // Three.js uses XRProjectionLayer (not XRWebGLLayer) when Layers API is available
    // We check after session attach by looking at the base layer type
    try {
      const baseLayer = (nativeXr as any).getBaseLayer?.();
      if (baseLayer && baseLayer.constructor?.name === 'XRProjectionLayer') {
        this._supportsLayers = true;
        this._layerTypes = new Set(['projection', 'quad', 'equirect']);
        Logger.info('XRWebGLBackend', 'Layers API supported (XRProjectionLayer detected)');
      }
    } catch {
      // getBaseLayer may not be available until after setSession
    }

    Logger.info('XRWebGLBackend', 'Session attached (WebGL binding)');
  }

  async detachSession(): Promise<void> {
    this._supportsLayers = false;
    this._layerTypes = new Set(['projection']);
    Logger.info('XRWebGLBackend', 'Session detached');
  }

  beginFrame(frame: XRFrame, refSpace: XRReferenceSpace): { views: XrViewRenderTarget[] } {
    const pose = frame.getViewerPose(refSpace);
    if (!pose) return { views: [] };

    const nativeXr = this._renderer.native.xr;
    const baseLayer = (nativeXr as any).getBaseLayer?.() ?? null;

    const views: XrViewRenderTarget[] = [];

    for (const view of pose.views) {
      const viewIdx = views.length;
      // Lazy-grow scratch arrays if the runtime reports more than 2 views
      // (not expected today but keeps the backend forward-compatible with
      // multi-view AR compositors and eye-tracked foveation layers).
      if (viewIdx >= this._viewMatrices.length) {
        this._viewMatrices.push(new Float32Array(16));
        this._projMatrices.push(new Float32Array(16));
      }
      while (this._viewports.length <= viewIdx) {
        this._viewports.push({ x: 0, y: 0, width: 0, height: 0 });
      }

      const viewport = this._viewports[viewIdx];
      viewport.x = 0; viewport.y = 0; viewport.width = 0; viewport.height = 0;
      if (baseLayer && 'getViewport' in baseLayer) {
        const vp = baseLayer.getViewport(view);
        if (vp) {
          viewport.x = vp.x; viewport.y = vp.y;
          viewport.width = vp.width; viewport.height = vp.height;
        }
      }

      // Derive view + projection matrices into pre-allocated buffers so the
      // GC never sees them.
      const viewMatrix = this._viewMatrices[viewIdx];
      const projMatrix = this._projMatrices[viewIdx];
      viewMatrix.set(view.transform.inverse.matrix);
      projMatrix.set(view.projectionMatrix);

      views.push({
        view,
        viewport,
        projectionMatrix: projMatrix,
        viewMatrix,
        depthRange: this.depthRange,
        color: { baseLayer },
        depth: undefined,
      });
    }

    return { views };
  }

  /**
   * Re-detect layer support (call after renderer.xr.setSession completes).
   */
  detectLayerSupport(): void {
    try {
      const baseLayer = (this._renderer.native.xr as any).getBaseLayer?.();
      if (baseLayer && baseLayer.constructor?.name === 'XRProjectionLayer') {
        this._supportsLayers = true;
        this._layerTypes = new Set(['projection', 'quad', 'equirect']);
      }
    } catch {
      // Silently ignore if not available
    }
  }
}
